import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Container, Row, Col, Card, Button, Form, Badge } from "react-bootstrap";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { FaArrowUp, FaYoutube, FaFilter, FaTimes, FaShoppingCart, FaRupeeSign, FaChevronDown, FaChevronUp } from "react-icons/fa";
import "@fortawesome/fontawesome-free/css/all.min.css";
import CrackersLoader from "./CrackersLoader";
import FloatingCart from "./FloatingCart";
import "./shop.css";

// Helper function to round to nearest whole number
const roundToNearestRupee = (amount) => {
  return Math.round(amount);
};

const Shop = () => {
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [storeClose, setStoreClose] = useState(0);
  const [counts, setCounts] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [discountvalue, setDiscountvalue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModal, setIsVideoModal] = useState(false);
  const [showFloatingCart, setShowFloatingCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [isMobileCategoryOpen, setIsMobileCategoryOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const searchBarRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isSmallScreen = window.innerWidth <= 768;
  const navigate = useNavigate();
  const location = useLocation();

  // Get category from URL params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryId = queryParams.get('category');
    if (categoryId && isInitialLoad) {
      setActiveCategory(categoryId);
      setIsInitialLoad(false);
    }
  }, [location.search, isInitialLoad]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(`${API_BASE_URL}quick_buy.php`);
        const { body } = response.data;

        console.log("API Response:", response.data);

        if (body) {
          const products = [];

          if (body.category && Array.isArray(body.category)) {
            body.category.forEach((category) => {
              if (category.product && Array.isArray(category.product)) {
                category.product.forEach((product) => {
                  products.push({
                    ...product,
                    category_id: category.category_id,
                    category_name: category.category_name,
                    is_non_discount: category.non_discount || "0",
                  });
                });
              } else {
                console.warn(`Category ${category.category_id} has no products or products is not an array:`, category);
              }
            });
          }

          console.log("Total products extracted:", products.length);

          setCategories(body.category || []);
          setAllProducts(products);
          setFilteredProducts(products);
          setDiscountvalue(body.discount_value?.[0]?.discount_value || 0);
          setStoreClose(Number(body.store_close || 0));

          const initialCounts = {};
          products.forEach((product) => {
            const storedCount = parseInt(localStorage.getItem(`count_${product.product_id}`)) || 0;
            if (storedCount > 0) {
              initialCounts[product.product_id] = storedCount;
            }
          });
          setCounts(initialCounts);

          const cartCount = Object.keys(initialCounts).length;
          setCartCount(cartCount);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Load cart items from localStorage
  useEffect(() => {
    const loadCartItems = () => {
      const items = JSON.parse(localStorage.getItem("cartItems")) || [];
      setCartItems(items);
    };
    loadCartItems();

    const handleCartUpdate = () => {
      loadCartItems();
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    window.addEventListener("storage", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, []);

  // Filter products based on category and search
  useEffect(() => {
    let filtered = [...allProducts];

    if (activeCategory !== 'all') {
      const categoryId = parseInt(activeCategory);
      filtered = filtered.filter(product => 
        parseInt(product.category_id) === categoryId
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.product_name && product.product_name.toLowerCase().includes(query)
      );
    }

    setFilteredProducts(filtered);
  }, [activeCategory, searchQuery, allProducts]);

  // Calculate totals
  const updateTotals = useCallback(() => {
    let discountTotal = 0;
    let nonDiscountTotal = 0;

    allProducts.forEach((product) => {
      const count = counts[product.product_id] || 0;
      if (count > 0) {
        const price = parseFloat(product.oldprice?.replace(/[^\d.-]/g, "")) || 0;
        if (!isNaN(price)) {
          if (product.is_non_discount === "0") {
            discountTotal += count * price;
          } else {
            nonDiscountTotal += count * price;
          }
        }
      }
    });

    setTotalAmount(discountTotal + nonDiscountTotal);
    return { discountTotal, nonDiscountTotal };
  }, [allProducts, counts]);

  useEffect(() => {
    updateTotals();
  }, [counts, updateTotals]);

  // Handle quantity changes
  const handleQuantityChange = (productId, value) => {
    const newCount = Math.max(0, parseInt(value) || 0);
    const newCounts = { ...counts, [productId]: newCount };
    
    if (newCount === 0) {
      delete newCounts[productId];
    }
    
    setCounts(newCounts);
    saveCountsToLocalStorage(newCounts);
  };

  const handleIncrement = (productId) => {
    const currentCount = counts[productId] || 0;
    const newCounts = { ...counts, [productId]: currentCount + 1 };
    setCounts(newCounts);
    saveCountsToLocalStorage(newCounts);
  };

  const handleDecrement = (productId) => {
    const currentCount = counts[productId] || 0;
    if (currentCount > 0) {
      const newCounts = { ...counts, [productId]: currentCount - 1 };
      if (newCounts[productId] === 0) {
        delete newCounts[productId];
      }
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
    }
  };

  const saveCountsToLocalStorage = (counts) => {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('count_'));
    keys.forEach(key => localStorage.removeItem(key));

    Object.entries(counts).forEach(([productId, count]) => {
      if (count > 0) {
        localStorage.setItem(`count_${productId}`, count);
      }
    });

    const addedItems = allProducts
      .filter(product => counts[product.product_id] > 0)
      .map(product => ({
        ...product,
        count: counts[product.product_id],
        category_id: product.category_id,
        is_non_discount: product.is_non_discount,
      }));

    localStorage.setItem("cartItems", JSON.stringify(addedItems));

    const totalUniqueProducts = Object.keys(counts).length;
    localStorage.setItem("cartCount", totalUniqueProducts);
    setCartCount(totalUniqueProducts);
    setCartItems(addedItems);

    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const HandleNavigate = (id) => {
    navigate(`/productdetail/${id}`);
  };

  // Image/Video modal handlers
  const handleImageClick = (imageSrc, youtubeUrl, name) => {
    setSelectedImage(imageSrc);
    setSelectedName(name);
    setSelectedVideo(null);
    setIsVideoModal(false);
    setShowModal(true);
  };

  const handleVideoIconClick = (youtubeUrl, name, imageSrc) => {
    let embedUrl = null;
    if (youtubeUrl) {
      const videoIdMatch = youtubeUrl.match(/(?:shorts\/|watch\?v=|v\/|embed\/)([a-zA-Z0-9_-]+)/);
      if (videoIdMatch && videoIdMatch[1]) {
        embedUrl = `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1`;
      }
    }
    setSelectedImage(imageSrc);
    setSelectedName(name);
    setSelectedVideo(embedUrl);
    setIsVideoModal(true);
    setShowModal(true);
  };

  // Toggle floating cart
  const toggleFloatingCart = () => {
    setShowFloatingCart(!showFloatingCart);
  };

  // Get total count of UNIQUE products in cart (not total quantity)
  const getTotalItems = () => {
    return Object.keys(counts).length;
  };

  // Calculate discount amount
  const discountRate = Math.round(discountvalue);
  let discountTotal = 0;
  let nonDiscountTotal = 0;

  allProducts.forEach((product) => {
    const count = counts[product.product_id] || 0;
    if (count > 0) {
      const price = parseFloat(product.oldprice?.replace(/[^\d.-]/g, "")) || 0;
      if (!isNaN(price)) {
        if (product.is_non_discount === "0") {
          discountTotal += count * price;
        } else {
          nonDiscountTotal += count * price;
        }
      }
    }
  });

  const discountAmount = (discountTotal * discountRate) / 100;
  const finalAmount = discountTotal - discountAmount + nonDiscountTotal;

  const clearSearch = () => {
    setSearchQuery("");
  };

  // Get total count of products in a category
  const getCategoryProductCount = (categoryId) => {
    const catId = String(categoryId);
    return allProducts.filter(p => String(p.category_id) === catId).length;
  };

  // Get total count of all products (with search filter for display)
  const getAllProductsCount = () => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return allProducts.filter(p => p.product_name && p.product_name.toLowerCase().includes(query)).length;
    }
    return allProducts.length;
  };

  // Toggle mobile category dropdown
  const toggleMobileCategory = () => {
    setIsMobileCategoryOpen(!isMobileCategoryOpen);
  };

  // Select category on mobile
  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setIsMobileCategoryOpen(false);
    const queryParams = new URLSearchParams(location.search);
    if (categoryId === 'all') {
      queryParams.delete('category');
    } else {
      queryParams.set('category', categoryId);
    }
    navigate({
      pathname: location.pathname,
      search: queryParams.toString()
    }, { replace: true });
  };

  // Handle category selection (for desktop)
  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    const queryParams = new URLSearchParams(location.search);
    if (categoryId === 'all') {
      queryParams.delete('category');
    } else {
      queryParams.set('category', categoryId);
    }
    navigate({
      pathname: location.pathname,
      search: queryParams.toString()
    }, { replace: true });
  };

  if (isLoading) {
    return <CrackersLoader />;
  }

  return (
    <div className="shop-page">
      <Header />

      {/* Store Banner */}
      <div className="shop-banner">
        <h1 className="shop-banner-title">Our Products</h1>
        <p className="shop-banner-subtitle">
          Browse our collection of premium crackers and festive items
        </p>
      </div>

      {/* Cart Icon - Floating */}
      {storeClose === 0 && getTotalItems() > 0 && (
        <div className="cart-floating-icon" onClick={toggleFloatingCart}>
          <span className="cart-icon-badge">{getTotalItems()}</span>
          <FaShoppingCart className="cart-icon" />
        </div>
      )}

      {/* Floating Cart Sidebar */}
      <FloatingCart 
        isOpen={showFloatingCart}
        onClose={toggleFloatingCart}
        cartItems={cartItems}
        discountvalue={discountvalue}
        onUpdateCart={setCartItems}
        onCountsUpdate={setCounts}
      />

      {/* Sidebar Component - Fixed Position (Desktop Only) */}
      <Sidebar 
        categories={categories}
        activeCategory={activeCategory}
        onCategorySelect={handleCategorySelect}
        filteredProductsCount={filteredProducts.length}
        searchQuery={searchQuery}
        getAllProductsCount={getAllProductsCount}
        getCategoryProductCount={getCategoryProductCount}
      />

      <Container fluid className="shop-container">
        {/* Products - Normal Flow (Natural Page Scroll) */}
        <div className="shop-products-wrapper">
          {/* Mobile Category Dropdown */}
          <div className="mobile-category-dropdown d-lg-none">
            <button 
              className="mobile-category-toggle" 
              onClick={toggleMobileCategory}
            >
              <div className="toggle-left">
                <FaFilter className="toggle-icon" />
                <span className="toggle-label">
                  {activeCategory === 'all' 
                    ? 'All Categories' 
                    : categories.find(c => String(c.category_id) === activeCategory)?.category_name || 'Categories'}
                </span>
              </div>
              <div className="toggle-right">
                <Badge className="category-count-badge">
                  {activeCategory === 'all' 
                    ? getAllProductsCount() 
                    : getCategoryProductCount(parseInt(activeCategory))}
                </Badge>
                {isMobileCategoryOpen ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </button>
            
            {isMobileCategoryOpen && (
              <div className="mobile-category-list">
                <div 
                  className={`mobile-category-item ${activeCategory === 'all' ? 'active' : ''}`}
                  onClick={() => selectCategory('all')}
                >
                  <span>All Products</span>
                  <Badge>{getAllProductsCount()}</Badge>
                </div>
                {categories.map((category) => {
                  const count = getCategoryProductCount(category.category_id);
                  return (
                    <div 
                      key={category.category_id}
                      className={`mobile-category-item ${activeCategory === String(category.category_id) ? 'active' : ''}`}
                      onClick={() => selectCategory(String(category.category_id))}
                    >
                      <span>{category.category_name}</span>
                      <Badge>{count}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Search Bar - Mobile */}
          <div className="mobile-search-bar d-lg-none">
            <div className="search-input-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                className="mobile-search-input"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="mobile-search-clear" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="cpw-category-header">
            <div className="cpw-category-info">
              <h3>{activeCategory !== 'all' && ` ${categories.find(c => String(c.category_id) === activeCategory)?.category_name}`}</h3>
              <div className="cpw-product-count">
                <span>{filteredProducts.length}</span> products available
              </div>
            </div>
          </div>

          {/* Products Grid - Natural Flow */}
          {filteredProducts.length > 0 ? (
            <Row className="g-3 g-md-4">
              {filteredProducts.map((product) => {
                const roundedOldPrice = product.oldprice ? roundToNearestRupee(parseFloat(String(product.oldprice).replace(/[^\d.-]/g, ""))) : 0;
                const roundedPrice = product.price ? roundToNearestRupee(parseFloat(String(product.price).replace(/[^\d.-]/g, ""))) : 0;
                const currentCount = counts[product.product_id] || 0;

                return (
                  <Col key={product.product_id} xs={6} md={4} lg={3}>
                    <Card className="product-card h-100">
                      <div className="product-image-wrapper">
                        <Card.Img 
                          variant="top" 
                          src={product.image} 
                          alt={product.product_name}
                          className="product-card-image"
                          onClick={() => HandleNavigate(product.product_id)}
                        />
                        {product.youtube_url && (
                          <div 
                            className="product-video-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVideoIconClick(product.youtube_url, product.product_name, product.image);
                            }}
                          >
                            <FaYoutube />
                          </div>
                        )}
                        {parseInt(product.is_non_discount, 10) === 0 && discountRate > 0 && (
                          <div className="product-discount-badge">
                            {discountRate}% OFF
                          </div>
                        )}
                      </div>
                      
                      <Card.Body className="product-card-body">
                        <div className="product-category-name">
                          {product.category_name}
                        </div>
                        <Card.Title className="product-card-title">
                          {product.product_name}
                        </Card.Title>
                        
                        <div className="product-price-section">
                          {parseInt(product.is_non_discount, 10) === 0 ? (
                            <>
                              <span className="product-old-price">
                                <FaRupeeSign /> {roundedOldPrice}
                              </span>
                              <span className="product-current-price">
                                <FaRupeeSign /> {roundedPrice}
                              </span>
                            </>
                          ) : (
                            <span className="product-current-price">
                              <FaRupeeSign /> {roundedOldPrice}
                            </span>
                          )}
                        </div>

                        <div className="product-qty-box">
                          <small className="text-muted">Qty per box: {product.qty_per_box || 'N/A'}</small>
                        </div>

                        {storeClose === 0 ? (
                          <div className="product-quantity-controls">
                            <div className="quantity-control-group">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="qty-btn"
                                onClick={() => handleDecrement(product.product_id)}
                              >
                                −
                              </Button>
                              <Form.Control
                                type="number"
                                className="qty-input"
                                value={currentCount || ''}
                                onChange={(e) => handleQuantityChange(product.product_id, e.target.value)}
                                min="0"
                                step="1"
                                placeholder="0"
                              />
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="qty-btn"
                                onClick={() => handleIncrement(product.product_id)}
                              >
                                +
                              </Button>
                            </div>
                            {currentCount > 0 && (
                              <div className="product-total-price">
                                Total: <FaRupeeSign /> 
                                {roundToNearestRupee(
                                  currentCount * (parseInt(product.is_non_discount, 10) === 0 ? roundedPrice : roundedOldPrice)
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="product-out-of-stock">
                            <Badge bg="danger">Store Closed</Badge>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="no-products-found text-center py-5">
              <i className="fas fa-search" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
              <h4 className="mt-3">No products found</h4>
              <p className="text-muted">
                {searchQuery ? `No products matching "${searchQuery}"` : 'Try adjusting your filters'}
              </p>
              {searchQuery && (
                <Button variant="primary" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              )}
            </div>
          )}

          {/* Bottom spacing */}
          <div style={{ height: '80px' }}></div>
        </div>
      </Container>

      {/* Floating Cart Summary - Only on mobile */}
      {storeClose === 0 && getTotalItems() > 0 && (
        <div className="floating-cart-summary d-md-none">
          <div className="floating-cart-link" onClick={toggleFloatingCart}>
            <div className="floating-cart-content">
              <div>
                <FaShoppingCart className="me-2" />
                <span>{getTotalItems()} items</span>
              </div>
              <div>
                <FaRupeeSign /> {roundToNearestRupee(finalAmount)}
                <i className="fas fa-chevron-right ms-2"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image/Video Modal */}
      {showModal && (
        <div className="shop-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="shop-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="shop-modal-close" onClick={() => setShowModal(false)}>
              <FaTimes />
            </button>
            <div className="shop-modal-body">
              {isVideoModal && selectedVideo ? (
                <div className="shop-video-container">
                  <iframe
                    src={selectedVideo}
                    title="Product Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <img src={selectedImage} alt={selectedName} className="shop-modal-image" />
              )}
              <div className="shop-modal-name">{selectedName}</div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top */}
      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop}>
          <FaArrowUp />
        </button>
      )}
    </div>
  );
};

export default Shop;